import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "@mui/system/styled";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { FoundMaterial } from "api/generatedApi";
import { useOrder } from "shared/table/use-order";
import { sortCompareLocalizedString } from "utils/compares";
import { TableHeaderConfig } from "shared/table/table-header-type";
import { TableHeader } from "shared/table/table-header";

const PRIORITY_CHARACTERS = ["!", "_"];

const normalizeMaterialName = (name: string | null | undefined) => {
  const trimmed = name?.trim() ?? "";
  const hasPriorityPrefix = PRIORITY_CHARACTERS.some((character) => trimmed.startsWith(character));
  const baseName = hasPriorityPrefix ? trimmed.slice(1) : trimmed;
  const sortName = baseName.toLocaleLowerCase("da");
  const formattedName = sortName ? sortName.charAt(0).toUpperCase() + sortName.slice(1) : "";

  return { hasPriorityPrefix, sortName, formattedName };
};

export interface Props {
  searchResults: FoundMaterial[];
  preselected: FoundMaterial | undefined;
  selectedMaterialProps: (material: FoundMaterial) => void;
}

export function SelectMaterialTable(props: Props) {
  type MaterialSortableId = "material" | "unit";

  const { searchResults, preselected, selectedMaterialProps } = props;
  const { t } = useTranslation();
  const [selectedMaterial, setSelectedMaterial] = useState<FoundMaterial | undefined>(preselected);
  const { direction, orderBy, getLabelProps } = useOrder<MaterialSortableId>("material");

  if (preselected) {
    selectedMaterialProps(preselected);
  }

  const TableCellStyled = styled(TableCell)`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `;

  const TableCellTypographyStyled = styled(Typography)`
    white-space: break-spaces;
    text-overflow: ellipsis;
    overflow: hidden;
  `;

  const selectMaterial = (event: React.MouseEvent<HTMLElement>, material: FoundMaterial) => {
    setSelectedMaterial(material);
    selectedMaterialProps(material);
  };

  const isMaterialSelected = (material: FoundMaterial) => {
    return selectedMaterial && selectedMaterial.eanNumber === material.eanNumber;
  };

  const sortedData = useMemo(() => {
    const normalized = searchResults.map((item) => {
      const { hasPriorityPrefix, sortName, formattedName } = normalizeMaterialName(item.name);

      return {
        item: { ...item, name: formattedName },
        hasPriorityPrefix,
        sortName,
      };
    });

    if (orderBy !== "material") {
      return normalized.map((entry) => entry.item);
    }

    normalized.sort((a, b) => {
      if (a.hasPriorityPrefix !== b.hasPriorityPrefix) {
        return a.hasPriorityPrefix ? -1 : 1;
      }

      return sortCompareLocalizedString("desc", a.sortName, b.sortName, "da", { sensitivity: "base" });
    });

    return normalized.map((entry) => entry.item);
  }, [searchResults, orderBy, direction]);

  const headerConfig: TableHeaderConfig<MaterialSortableId>[] = [
    {
      id: "material",
      title: t("content.measurements.create.selectMaterialStep.table.material"),
      sortable: true,
      testid: "create-workitem-material-title",
      alignment: "left",
    },
  ];

  const formatOperationTime = (milliseconds: number): string => {
    const hours = Math.floor(milliseconds / 3600000);
    const minutes = Math.floor((milliseconds % 3600000) / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <TableContainer sx={{ height: "100%", overflowY: "auto" }} component={Paper}>
      <Table sx={{ tableLayout: "fixed" }}>
        <TableHeader headerConfig={headerConfig} getLabelProps={getLabelProps} />
        <TableBody>
          {sortedData.map((mat, index) => {
            return (
              <TableRow data-testid={`create-workitem-material-search-result-${mat.name}-line`} key={index} onClick={(event) => selectMaterial(event, mat)} selected={isMaterialSelected(mat)}>
                <TableCellStyled sx={{ pr: 0, color: "primary.main" }}>
                  <TableCellTypographyStyled sx={{ color: "black" }} data-testid={`create-workitem-material-search-result-${mat.name}-name`} variant="body2">
                    {mat.name}
                  </TableCellTypographyStyled>
                  {mat.operationTimeMilliseconds !== 0 && (
                    <Typography data-testid={`create-workitem-material-search-reesult-${mat.name}-ean`} variant="caption">
                      {mat.eanNumber + "; " + formatOperationTime(mat.operationTimeMilliseconds ?? 0)}
                    </Typography>
                  )}
                  {mat.operationTimeMilliseconds === 0 && (
                    <Typography data-testid={`create-workitem-material-search-reesult-${mat.name}-ean`} variant="caption">
                      {mat.eanNumber}
                    </Typography>
                  )}
                </TableCellStyled>
                {/* KTH 14/4-23 hide until we have units <TableCellStyled
                  data-testid={`create-workitem-material-search-result-${mat.name}-unit`}
                  align="center"
                  sx={{ pl: 1, color: "primary.main" }}
                >
                  Test
                </TableCellStyled> */}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
